/**
 * Step 2 — Internet connectivity check.
 *
 * Polls the backend (via the main process) until it is reachable, then enables
 * Continue automatically. Uses `navigator.onLine` as a fast hint and the real
 * reachability probe as the source of truth.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import sx from '../setupStyles';
import { setupApi, apiBaseUrl } from '../desktopApi';

const POLL_MS = 3000;

export default function Connectivity({ goBack, goNext, patchCtx }) {
  const [online, setOnline] = useState(false);
  const [checking, setChecking] = useState(true);
  const timer = useRef(null);

  const check = useCallback(async () => {
    setChecking(true);
    try {
      const res = await setupApi.checkConnectivity(apiBaseUrl);
      const ok = !!(res && res.online);
      setOnline(ok);
      if (ok) patchCtx({ online: true });
    } catch (_) {
      setOnline(false);
    } finally {
      setChecking(false);
    }
  }, [patchCtx]);

  useEffect(() => {
    check();
    timer.current = setInterval(check, POLL_MS);
    return () => clearInterval(timer.current);
  }, [check]);

  return (
    <>
      <Box sx={sx.body}>
        <Box sx={sx.centerCol}>
          <Typography variant="h6" fontWeight={600}>
            Checking your internet connection
          </Typography>
          {online ? (
            <Alert severity="success">You&apos;re connected. You can continue.</Alert>
          ) : (
            <Alert severity="warning">
              Internet connection required. Please connect to the internet to complete the initial
              setup — Continue will enable automatically once a connection is detected.
            </Alert>
          )}
          <Stack direction="row" spacing={1} alignItems="center" color="text.secondary">
            {checking && <CircularProgress size={16} />}
            <Typography variant="body2" color="text.secondary">
              {checking ? 'Testing connection…' : online ? 'Connected' : 'Waiting for connection…'}
            </Typography>
          </Stack>
        </Box>
      </Box>
      <Box sx={sx.footer}>
        <Button color="inherit" onClick={goBack}>
          Back
        </Button>
        <Stack direction="row" spacing={1}>
          <Button onClick={check} disabled={checking}>
            Check again
          </Button>
          <Button variant="contained" onClick={goNext} disabled={!online}>
            Continue
          </Button>
        </Stack>
      </Box>
    </>
  );
}
